// ============================================================
// ExposureCustomBuilder - the exposure picker's custom builder
// ============================================================
// An axis (grouped by family), the value its kind takes - a signed
// number, nothing for a flag, an expression, a timeless condition, or a
// location - then radius, cap and rounds. What it writes, and what it
// refuses, is `customExposureCode`.

import { useState } from 'react';
import NumberInput from './NumberInput';
import { HIT_LOCATIONS } from '../engine/constants';
import { AXES, type Axis } from '../engine/exposures';
import { customExposureCode, type CustomExposure, type SharedExposureFields } from './exposureBuilder';
import common from '../styles/common.module.css';
import styles from '../styles/AddConditionModal.module.css';

const FAMILIES: { id: (typeof AXES)[number]['family']; label: string }[] = [
  { id: 'to-hit', label: 'Being attacked' },
  { id: 'on-hit', label: 'Being hit' },
  { id: 'hazard', label: 'Touching them' },
  { id: 'gating', label: 'Daring to' },
  { id: 'reaction', label: 'Their attacks' },
  { id: 'aura', label: 'Around them' },
];

const INITIAL: CustomExposure = {
  axis: 'hit',
  value: 20,
  expr: '1d5R',
  condCode: 'S1',
  loc: 'body',
  radius: 0,
  cap: 0,
  rounds: 0,
};

interface Props {
  shared: SharedExposureFields;
  onAdd: (code: string) => void;
}

export function ExposureCustomBuilder({ shared, onAdd }: Props) {
  const [c, setC] = useState<CustomExposure>(INITIAL);
  const set =
    <K extends keyof CustomExposure>(key: K) =>
    (v: CustomExposure[K]) =>
      setC((p) => ({ ...p, [key]: v }));
  const axisDef = AXES.find((a) => a.id === c.axis)!;
  const add = () => {
    const code = customExposureCode(c, shared);
    if (code) onAdd(code);
  };
  return (
    <div className={styles.categorySection}>
      <div className={styles.categoryHeader}>Custom</div>
      <div className={styles.presetRow} style={{ flexWrap: 'wrap', gap: 6 }}>
        <AxisSelect axis={c.axis} onChange={set('axis')} />
        <AxisValueField kind={axisDef.kind} c={c} set={set} />
        <CountField
          label="Radius (m)"
          aria="Exposure radius"
          value={c.radius}
          onChange={set('radius')}
          title="An aura: read by whoever stands within this many metres, as if their own target wore it. 0 = worn"
        />
        <CountField
          label="Cap"
          aria="Exposure cap"
          value={c.cap}
          onChange={set('cap')}
          title="When several exposures stack on this axis, the total never passes ±cap. 0 = uncapped"
        />
        <CountField
          label="Rounds"
          aria="Exposure rounds"
          value={c.rounds}
          onChange={set('rounds')}
          title="0 = until removed"
        />
        <button type="button" className={common.btnSmall} onClick={add} data-testid="exposure-add-custom">
          Add
        </button>
      </div>
      <div className={styles.subPanelLabel} style={{ marginTop: 4 }}>
        {axisDef.hint}
      </div>
    </div>
  );
}

/** The axis, grouped by family. */
function AxisSelect({ axis, onChange }: { axis: Axis; onChange: (axis: Axis) => void }) {
  return (
    <label className={common.label}>
      Axis
      <select
        className={common.input}
        aria-label="Exposure axis"
        value={axis}
        onChange={(e) => onChange(e.target.value as Axis)}
      >
        {FAMILIES.map((f) => (
          <optgroup key={f.id} label={f.label}>
            {AXES.filter((a) => a.family === f.id).map((a) => (
              <option key={a.id} value={a.id} title={a.hint}>
                {a.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

type Setter = <K extends keyof CustomExposure>(key: K) => (v: CustomExposure[K]) => void;

/** The value field for the axis's kind; a flag has none. */
function AxisValueField({ kind, c, set }: { kind: string; c: CustomExposure; set: Setter }) {
  if (kind === 'number') {
    return (
      <label className={common.label}>
        Value
        <NumberInput
          className={common.input}
          style={{ width: 64 }}
          aria-label="Exposure value"
          value={c.value}
          allowNegative
          onChange={(v: number) => set('value')(v)}
        />
      </label>
    );
  }
  if (kind === 'cond') {
    return (
      <TextField
        label="Condition (timeless code)"
        aria="Exposure condition code"
        width={120}
        value={c.condCode}
        onChange={set('condCode')}
        title="As the picker stores it: S1, Fi, BL, F2, Pen:all=-10 - never with a clock"
      />
    );
  }
  if (kind === 'loc') return <LocationField value={c.loc} onChange={set('loc')} />;
  if (kind === 'expr') {
    return (
      <TextField
        label={c.axis === 'grasp' ? 'Test (S or Ag, ±N)' : 'Damage (e.g. 1d5R)'}
        aria="Exposure expression"
        width={90}
        value={c.expr}
        onChange={set('expr')}
      />
    );
  }
  return null;
}

function LocationField({ value, onChange }: { value: string; onChange: (loc: string) => void }) {
  return (
    <label className={common.label}>
      Location
      <select
        className={common.input}
        aria-label="Exposure hit location"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {HIT_LOCATIONS.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}

interface TextFieldProps {
  label: string;
  aria: string;
  width: number;
  value: string;
  onChange: (value: string) => void;
  title?: string;
}

function TextField({ label, aria, width, value, onChange, title }: TextFieldProps) {
  return (
    <label className={common.label}>
      {label}
      <input
        className={common.input}
        style={{ width }}
        aria-label={aria}
        title={title}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

interface CountFieldProps {
  label: string;
  aria: string;
  title?: string;
  value: number;
  onChange: (value: number) => void;
}

/** A labelled count, 0 or more. */
export function CountField({ label, aria, title, value, onChange }: CountFieldProps) {
  return (
    <label className={common.label}>
      {label}
      <NumberInput
        className={common.input}
        style={{ width: 56 }}
        aria-label={aria}
        title={title}
        value={value}
        min={0}
        onChange={(v: number) => onChange(v)}
      />
    </label>
  );
}
